import moment from "moment";

// ---- Campaign field limits (mirror the backend column sizes) ----
export const CAMPAIGN_LIMITS = {
    name: 200,
    description: 2000,
};

export const ASSET_LIMITS = {
    text1: 500,
    text2: 2000,
    text3: 3000,
    imageUrl: 4000,
    onImageClickUrl: 4000,
};

// ---- Asset image constraints ----
export const IMAGE_ASPECT_RATIO = 3; // width : height = 3 : 1
export const IMAGE_MAX_SIZE_BYTES = 1024 * 1024; // 1MB (final, after processing)
export const IMAGE_SOURCE_MAX_BYTES = 25 * 1024 * 1024; // guard the browser's memory only
export const BANNER_TARGET_WIDTH = 1500; // output banner width (3:1)
export const BANNER_TARGET_HEIGHT = BANNER_TARGET_WIDTH / IMAGE_ASPECT_RATIO; // 500
const BANNER_QUALITY_STEPS = [0.82, 0.7, 0.6, 0.5, 0.4];
export const IMAGE_DISCLAIMER =
    "Choose any image; it will be automatically cropped to a 3:1 banner and compressed. You can adjust the crop before applying.";

/** Reads a File into a data URL (used to feed the cropper). */
export const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Could not read the selected file."));
        reader.readAsDataURL(file);
    });

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("The selected file is not a valid image."));
        image.src = src;
    });

const canvasToBlob = (canvas, type, quality) =>
    new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), type, quality));

/**
 * Crops the region given by react-easy-crop (`cropPixels`) from `imageSrc`,
 * scales it into the fixed 3:1 banner canvas and compresses it, lowering the
 * quality until the result is <= 1MB. Returns a ready-to-upload File.
 */
export const getCroppedBannerFile = async (imageSrc, cropPixels, baseName = "banner") => {
    const image = await loadImage(imageSrc);
    const canvas = document.createElement("canvas");
    canvas.width = BANNER_TARGET_WIDTH;
    canvas.height = BANNER_TARGET_HEIGHT;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
        image,
        cropPixels.x,
        cropPixels.y,
        cropPixels.width,
        cropPixels.height,
        0,
        0,
        BANNER_TARGET_WIDTH,
        BANNER_TARGET_HEIGHT
    );

    // Prefer WEBP (smaller + supports transparency); fall back to JPEG if the
    // browser can't encode WEBP via canvas.
    let type = "image/webp";
    let extension = "webp";
    let chosenBlob = null;
    for (const quality of BANNER_QUALITY_STEPS) {
        // eslint-disable-next-line no-await-in-loop
        const blob = await canvasToBlob(canvas, type, quality);
        if (!blob) break;
        chosenBlob = blob;
        if (blob.size <= IMAGE_MAX_SIZE_BYTES) break;
    }
    if (!chosenBlob) {
        type = "image/jpeg";
        extension = "jpg";
        for (const quality of BANNER_QUALITY_STEPS) {
            // eslint-disable-next-line no-await-in-loop
            const blob = await canvasToBlob(canvas, type, quality);
            if (!blob) continue;
            chosenBlob = blob;
            if (blob.size <= IMAGE_MAX_SIZE_BYTES) break;
        }
    }
    if (!chosenBlob) {
        throw new Error("Could not process the image.");
    }
    return new File([chosenBlob], `${baseName}.${extension}`, { type });
};

/**
 * Serializes a date (JS Date or moment) into an ISO-8601 instant *with timezone
 * offset* (e.g. `2026-07-01T14:00:00-05:00`), which is what the backend expects.
 * The user captures the date/time in their local zone; keeping the offset makes
 * the backend resolve the correct absolute instant (persisted/compared in UTC).
 * Never emit a zoneless "wall clock" value — the backend rejects it on filters
 * and misinterprets it on create/update. Returns null for empty values.
 */
export const toIsoInstant = (date) => {
    if (!date) return null;
    return moment(date).format(); // ISO-8601 including the local timezone offset
};

/**
 * Builds the `filter` object sent to `GET /campaigns` from the current UI
 * filter state. Combined with AND on the backend.
 */
export const buildCampaignFilter = ({
    quickFilterValue,
    startDatetimeFrom,
    startDatetimeTo,
    endDatetimeFrom,
    endDatetimeTo,
    status,
} = {}) => {
    const filter = {};
    if (quickFilterValue) filter.quickFilterValue = quickFilterValue;
    if (startDatetimeFrom) filter.startDatetimeFrom = toIsoInstant(startDatetimeFrom);
    if (startDatetimeTo) filter.startDatetimeTo = toIsoInstant(startDatetimeTo);
    if (endDatetimeFrom) filter.endDatetimeFrom = toIsoInstant(endDatetimeFrom);
    if (endDatetimeTo) filter.endDatetimeTo = toIsoInstant(endDatetimeTo);
    if (status) filter.status = status;
    return filter;
};

/**
 * Builds the in-session clone payload carried across detail -> add -> edit.
 * Keeps the original assets (with their original imageUrls) so their images can
 * be copied into the new campaign's storage folder when the clone is saved.
 */
export const buildCloneStateFromCampaign = (campaign) => ({
    basic: {
        name: campaign.name || "",
        description: campaign.description || "",
        startDatetime: campaign.startDatetime || null,
        endDatetime: campaign.endDatetime || null,
    },
    assets: (campaign.assets || []).map((asset) => ({
        text1: asset.text1 || "",
        text2: asset.text2 || "",
        text3: asset.text3 || "",
        imageUrl: asset.imageUrl || "",
        onImageClickUrl: asset.onImageClickUrl || "",
    })),
    newCampaignId: null,
});

/**
 * Maps the cloned source assets into edit-form asset values. Each asset is a
 * brand-new asset (`id: null`) whose `imageUrl` still points to the original
 * campaign's image; `sourceImageUrl` marks it as pending to be copied into the
 * new campaign's storage folder on save (unless the user replaces the image).
 */
export const mapClonedAssets = (assets = []) =>
    assets.map((asset) => ({
        id: null,
        text1: asset.text1 || "",
        text2: asset.text2 || "",
        text3: asset.text3 || "",
        imageUrl: asset.imageUrl || "",
        onImageClickUrl: asset.onImageClickUrl || "",
        sourceImageUrl: asset.imageUrl || "",
    }));

/**
 * Extracts the original file name from a Firebase Storage download URL so a
 * cloned copy can keep a meaningful name/extension. Falls back to "image".
 */
export const getStorageFileNameFromUrl = (url) => {
    try {
        const encodedPath = url.split("/o/")[1].split("?")[0];
        const path = decodeURIComponent(encodedPath);
        return path.substring(path.lastIndexOf("/") + 1) || "image";
    } catch (error) {
        return "image";
    }
};

/**
 * Maps a campaign entity into the payload shape accepted by POST/PUT.
 * `includeAssets` controls whether the assets array is serialized (assets are
 * only edited from the edit page, never on creation).
 */
export const buildCampaignPayload = (values, { id = null, status, includeAssets = false } = {}) => {
    const payload = {
        id,
        name: values.name?.trim(),
        description: values.description?.trim() || null,
        startDatetime: toIsoInstant(values.startDatetime),
        endDatetime: toIsoInstant(values.endDatetime),
    };
    if (status) payload.status = status;
    if (includeAssets) {
        payload.assets = (values.assets || []).map((asset) => ({
            id: asset.id || null,
            text1: asset.text1?.trim() || null,
            text2: asset.text2?.trim() || null,
            text3: asset.text3?.trim() || null,
            imageUrl: asset.imageUrl,
            onImageClickUrl: asset.onImageClickUrl?.trim() || null,
        }));
    }
    return payload;
};
